import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Plus, Search, Edit, Trash2, Shield, User, Eye, EyeOff, Lock, AlertCircle } from 'lucide-react';
import { useNotifications } from '@/contexts/NotificationContext';
import { cn } from '@/lib/utils';
import usersApi from '@/services/users';

const ROLE_DESCRIPTIONS = {
  Admin: "Full system access. Can manage users, settings, and all project data.",
  Manager: "Can manage projects, view HR dashboards, and approve operational requests.",
  Engineer: "Access to assigned projects, daily reports, and site documentation.",
  Secretary: "Can manage correspondence, upload files, and view project lists.",
  Accountant: "Access to financial modules, invoices, LPOs, and expense tracking.",
  Employee: "Limited access. Can view personal profile and attendance."
};

const UsersPage = () => {
  const { addNotification } = useNotifications();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'Employee',
    phone: '',
    department: '',
    password: '',
    confirmPassword: ''
  });

  // UI State
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [changePasswordMode, setChangePasswordMode] = useState(false);

  // ✅ Load Users from API
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await usersApi.getUsersList();
      if (response.success) {
        setUsers(response.data || []);
      }
    } catch (error) {
      console.error("Failed to load users", error);
      addNotification("Error", "Failed to load users data", "error");
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Name
    if (!formData.name.trim()) newErrors.name = "Full Name is required";
    
    // Email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Invalid email format";
    } else if (!editingUser && users.some(u => u.email.toLowerCase() === formData.email.toLowerCase())) {
      newErrors.email = "Email already in use";
    }

    // Password Validation
    const shouldValidatePassword = !editingUser || changePasswordMode;
    
    if (shouldValidatePassword) {
      if (!formData.password) {
        newErrors.password = "Password is required";
      } else if (formData.password.length < 8) {
        newErrors.password = "Password must be at least 8 characters";
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
      }
    }

    // Role
    if (!formData.role) newErrors.role = "Role selection is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveUser = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);

      const userData = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        department: formData.department,
        phone: formData.phone
      };

      if (editingUser) {
        // Update existing user
        userData.id = editingUser.id;
        
        // Only include password if changing
        if (changePasswordMode && formData.password) {
          userData.password = formData.password;
        }

        const response = await usersApi.updateUser(userData);
        if (response.success) {
          await loadUsers();
          addNotification("User Updated", `User ${formData.name} details were updated successfully.`, "info");
        }
      } else {
        // Create new user
        userData.password = formData.password;

        const response = await usersApi.createUser(userData);
        if (response.success) {
          await loadUsers();
          addNotification("User Created", `New user ${formData.name} added to the system.`, "success");
        }
      }

      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error("Save user error:", error);
      addNotification("Error", error.message || "Failed to save user", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure? This user will lose access to the system immediately.")) return;
    
    try {
      const response = await usersApi.deleteUser(id);
      if (response.success) {
        await loadUsers();
        addNotification("User Deleted", "User access removed.", "warning");
      }
    } catch (error) {
      addNotification("Error", "Failed to delete user", "error");
    }
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      role: user.role,
      phone: user.phone || '',
      department: user.department || '',
      password: '',
      confirmPassword: ''
    });
    setChangePasswordMode(false);
    setErrors({});
    setIsDialogOpen(true);
  };

  const openCreate = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      role: 'Employee',
      phone: '',
      department: '',
      password: '',
      confirmPassword: ''
    });
    setErrors({});
    setChangePasswordMode(false);
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const filteredUsers = users.filter(u => 
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Password Strength Indicator
  const getPasswordStrength = (pass) => {
    if (!pass) return 0;
    let strength = 0;
    if (pass.length >= 8) strength += 1;
    if (pass.match(/[A-Z]/)) strength += 1;
    if (pass.match(/[0-9]/)) strength += 1;
    if (pass.match(/[^A-Za-z0-9]/)) strength += 1;
    return strength;
  };
  
  const strength = getPasswordStrength(formData.password);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-safety-orange"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet><title>User Management - Stone Beam</title></Helmet>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-navy-steal">Users</h1>
            <p className="text-stone-500">Manage system access, roles, and permissions</p>
          </div>
          <Button onClick={openCreate} className="bg-safety-orange hover:bg-orange-600 text-white shadow-md">
            <Plus className="w-4 h-4 mr-2" /> Add New User
          </Button>
        </div>

        {/* Search Bar */}
        <div className="bg-white p-4 rounded-xl shadow-sm border border-stone-100">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input 
              placeholder="Search users by name or email..." 
              value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)} 
              className="pl-10 bg-slate-50 border-stone-200"
            />
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-xl shadow-sm border border-stone-200 overflow-hidden">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="w-[300px]">User Profile</TableHead>
                <TableHead>Role & Permissions</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map(user => (
                <TableRow key={user.id} className="hover:bg-slate-50/50">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10 border border-stone-100">
                        <AvatarImage src={user.photo} />
                        <AvatarFallback className="bg-navy-steal text-white">{user.name.charAt(0).toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium text-navy-steal">{user.name}</div>
                        <div className="text-xs text-stone-500">{user.email}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <Shield className="w-3.5 h-3.5 text-safety-orange" />
                        <span className="font-medium text-sm text-stone-700">{user.role}</span>
                      </div>
                      <span className="text-[10px] text-stone-400 max-w-[200px] truncate" title={ROLE_DESCRIPTIONS[user.role]}>
                        {ROLE_DESCRIPTIONS[user.role]}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="inline-flex items-center px-2 py-1 rounded-md bg-slate-100 text-xs font-medium text-stone-600">
                      {user.department || 'General'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1 text-xs text-stone-500">
                      {user.phone || <span className="text-stone-300 italic">No phone</span>}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-stone-500 hover:text-navy-steal hover:bg-slate-100" onClick={() => openEdit(user)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 text-stone-400 hover:text-red-600 hover:bg-red-50" 
                        onClick={() => handleDelete(user.id)} 
                        disabled={user.role === 'Admin' && users.filter(u => u.role === 'Admin').length === 1}
                        title={user.role === 'Admin' ? "Cannot delete last admin" : "Delete User"}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filteredUsers.length === 0 && (
                 <TableRow>
                    <TableCell colSpan={5} className="h-32 text-center text-stone-400">
                       No users found matching your search.
                    </TableCell>
                 </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Dialog - continues in next part... */}
      {/* Add/Edit User Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-navy-steal flex items-center gap-2">
              {editingUser ? <Edit className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
              {editingUser ? 'Edit User Details' : 'Add New User'}
            </DialogTitle>
            <DialogDescription>
              {editingUser ? 'Update user information and permissions.' : 'Create a new user account with system access credentials.'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-6 py-4">
            
            {/* Section 1: Personal Info */}
            <div className="space-y-4">
               <h3 className="text-sm font-semibold text-stone-900 flex items-center gap-2 border-b pb-1">
                 <User className="w-4 h-4" /> Personal Information
               </h3>
               <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="name">Full Name <span className="text-red-500">*</span></Label>
                    <Input 
                      id="name" 
                      placeholder="e.g. John Doe"
                      value={formData.name} 
                      onChange={(e) => setFormData({...formData, name: e.target.value})} 
                      className={errors.name ? "border-red-500 bg-red-50" : ""}
                    />
                    {errors.name && <span className="text-xs text-red-500">{errors.name}</span>}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input 
                      id="phone" 
                      placeholder="+971 50 ..."
                      value={formData.phone} 
                      onChange={(e) => setFormData({...formData, phone: e.target.value})} 
                    />
                  </div>
               </div>
            </div>

            {/* Section 2: Role & Dept */}
            <div className="space-y-4">
               <h3 className="text-sm font-semibold text-stone-900 flex items-center gap-2 border-b pb-1">
                 <Shield className="w-4 h-4" /> Role & Permissions
               </h3>
               <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label>Role <span className="text-red-500">*</span></Label>
                    <Select value={formData.role} onValueChange={(val) => setFormData({...formData, role: val})}>
                      <SelectTrigger className={errors.role ? "border-red-500 bg-red-50" : ""}>
                        <SelectValue placeholder="Select Role" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.keys(ROLE_DESCRIPTIONS).map(role => (
                          <SelectItem key={role} value={role}>{role}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.role && <span className="text-xs text-red-500">{errors.role}</span>}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="department">Department</Label>
                    <Input 
                      id="department" 
                      placeholder="e.g. Engineering"
                      value={formData.department} 
                      onChange={(e) => setFormData({...formData, department: e.target.value})} 
                    />
                  </div>
               </div>
               
               {/* Role Description Helper */}
               {formData.role && (
                 <div className="bg-blue-50 text-blue-700 text-xs p-3 rounded-md flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    <p><strong>{formData.role}:</strong> {ROLE_DESCRIPTIONS[formData.role]}</p>
                 </div>
               )}
            </div>

            {/* Section 3: Credentials */}
            <div className="space-y-4">
               <div className="flex items-center justify-between border-b pb-1">
                 <h3 className="text-sm font-semibold text-stone-900 flex items-center gap-2">
                   <Lock className="w-4 h-4" /> Login Credentials
                 </h3>
                 {editingUser && (
                   <div className="flex items-center gap-2">
                     <input 
                       type="checkbox" 
                       id="changePass" 
                       className="rounded border-stone-300 text-safety-orange focus:ring-safety-orange"
                       checked={changePasswordMode}
                       onChange={(e) => setChangePasswordMode(e.target.checked)}
                     />
                     <Label htmlFor="changePass" className="text-xs font-normal cursor-pointer select-none">Reset Password</Label>
                   </div>
                 )}
               </div>

               <div className="grid gap-2">
                  <Label htmlFor="email">Email Address <span className="text-red-500">*</span></Label>
                  <Input 
                    id="email" 
                    type="email" 
                    placeholder="john@company.com"
                    value={formData.email} 
                    onChange={(e) => setFormData({...formData, email: e.target.value})} 
                    className={errors.email ? "border-red-500 bg-red-50" : ""}
                  />
                  {errors.email && <span className="text-xs text-red-500">{errors.email}</span>}
               </div>

               {(!editingUser || changePasswordMode) && (
                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
                    <div className="grid gap-2">
                      <Label htmlFor="password">Password <span className="text-red-500">*</span></Label>
                      <div className="relative">
                        <Input 
                          id="password" 
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={(e) => setFormData({...formData, password: e.target.value})}
                          className={`pr-10 ${errors.password ? "border-red-500 bg-red-50" : ""}`}
                          placeholder="Min 8 chars"
                        />
                        <button 
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                          onClick={() => setShowPassword(!showPassword)}
                          tabIndex={-1}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {/* Password Strength Indicator */}
                      {formData.password && (
                         <div className="flex gap-1 h-1 mt-1">
                            <div className={`flex-1 rounded-full transition-colors ${strength >= 1 ? "bg-red-400" : "bg-stone-200"}`} />
                            <div className={`flex-1 rounded-full transition-colors ${strength >= 2 ? "bg-orange-400" : "bg-stone-200"}`} />
                            <div className={`flex-1 rounded-full transition-colors ${strength >= 3 ? "bg-yellow-400" : "bg-stone-200"}`} />
                            <div className={`flex-1 rounded-full transition-colors ${strength >= 4 ? "bg-green-500" : "bg-stone-200"}`} />
                         </div>
                      )}
                      {errors.password && <span className="text-xs text-red-500">{errors.password}</span>}
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="confirmPassword">Confirm Password <span className="text-red-500">*</span></Label>
                      <div className="relative">
                        <Input 
                          id="confirmPassword" 
                          type={showConfirmPassword ? "text" : "password"}
                          value={formData.confirmPassword}
                          onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                          className={`pr-10 ${errors.confirmPassword ? "border-red-500 bg-red-50" : ""}`}
                          placeholder="Re-enter password"
                        />
                        <button 
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          tabIndex={-1}
                        >
                          {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {errors.confirmPassword && <span className="text-xs text-red-500">{errors.confirmPassword}</span>}
                    </div>
                 </div>
               )}
            </div>

          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="sm:mr-auto">
              Cancel
            </Button>
            <Button 
              onClick={handleSaveUser} 
              disabled={submitting}
              className="bg-navy-steal hover:bg-charcoal text-white min-w-[120px]"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                editingUser ? 'Save Changes' : 'Create User'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UsersPage;
